import { useState, useCallback, useRef } from "react";
import legacy, {
  clear_map,
  g_price_markers,
  draw_land_polygon,
  clear_land_polygons,
  clear_arch_polygons,
  slt,
} from "../../../utils/legacy";
import { estateExploreService } from "../../../services/estateExplore/estateExploreService";
import { createOverlayMode } from "../utils/mapOverlay";

/**
 * 매물탐색 맵 연동 hook
 *
 * legacy 카카오맵과 React 상태를 연결한다.
 * - handleMap: overlay 모드 on/off
 * - drawPolygonForEstate: 매물 폴리곤 그리기
 * - isMove: 맵 이동 후 새로고침 필요 여부
 * - requestSelectedGroup: 마커 클릭 → 그룹 데이터 로드
 *
 * @param {Object} opts
 * @param {React.MutableRefObject} opts.payloadRef
 * @param {React.MutableRefObject} opts.sideviewRef
 * @param {Function} opts.loadData
 * @param {Function} opts.fetchList
 * @param {Object}   opts.MarkerClass    — ExplorerMarker 또는 ExplorerMarkerMobile
 * @param {Function} opts.onMapInteraction — 맵 조작 시 콜백 (바텀시트 닫기 등)
 * @param {Function} opts.onMarkerSelect  — 마커 클릭 시 콜백 (바텀시트 열기 등)
 */
export default function useExploreMap({
  payloadRef,
  sideviewRef,
  loadData,
  fetchList,
  MarkerClass,
  onMapInteraction,
  onMarkerSelect,
  onOpenDetail,
}) {
  const [isMove, setIsMove] = useState(false);
  const [mapCount, setMapCount] = useState(null);

  const overlay = useRef(null);
  const currentSelectedPnuRef = useRef(null);
  const savedMapStateRef = useRef(null);
  const [zoomRequired, setZoomRequired] = useState(false);

  // 맵 레벨 기준 줌 제한 (레벨 7 초과 시 토스트)
  const MAX_MAP_LEVEL = 7;

  /**
   * 현재 맵 바운드 + 필터를 합친 payload 생성
   */
  const getMapPayload = useCallback(() => {
    const bounds = legacy.discoMap.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const center = legacy.discoMap.getSltCenter();
    const map_lv = legacy.map.getLevel();

    // bounds 면적 체크 — 프론트에서 1차 차단
    setZoomRequired(map_lv > MAX_MAP_LEVEL);

    return {
      ...payloadRef.current,
      swLat: sw.getLat(),
      swLng: sw.getLng(),
      neLat: ne.getLat(),
      neLng: ne.getLng(),
      map_lv,
      centerLat: center.getLat(),
      centerLng: center.getLng(),
    };
  }, [payloadRef]);

  /**
   * 매물 토지/건축물 폴리곤 그리기
   */
  const drawPolygonForEstate = useCallback((markerData) => {
    if (!markerData) return null;

    // 다른 매물 선택 시 기존 폴리곤 제거
    if (
      currentSelectedPnuRef.current &&
      currentSelectedPnuRef.current !== markerData?.pnu
    ) {
      clear_land_polygons();
      clear_arch_polygons();
    }

    const addressOpen = markerData.addressOpen || markerData.address_open;
    const selectType = markerData.select_type || markerData.selectType;

    if (
      markerData.pnu &&
      markerData.pnu !== "0" &&
      markerData.pnu !== "" &&
      (addressOpen == 1 || selectType === "auction")
    ) {
      if (slt) {
        slt.pnu = markerData.pnu;
        slt.address = markerData.address || "";
        slt.x = markerData.lng || "";
        slt.y = markerData.lat || "";
        slt.uuid =
          markerData.u ||
          markerData.group_key ||
          markerData.markerKey || markerData.marker_key ||
          "";
      }

      if (draw_land_polygon) draw_land_polygon(markerData.pnu);

      currentSelectedPnuRef.current = markerData.pnu;
      return markerData.pnu;
    } else {
      clear_land_polygons();
      clear_arch_polygons();
      currentSelectedPnuRef.current = null;
      return null;
    }
  }, []);

  /**
   * 마커 클릭 시 그룹 데이터 로드 (single/cluster 공용)
   */
  const requestSelectedGroup = useCallback(
    (params) => {
      if (onMarkerSelect) onMarkerSelect(params);

      if (!params.conditions) return;

      // 모든 마커 클릭은 conditions 방식으로 통일
      // region 조건이 있으면 현재 화면 bounds를 포함 (행정구역 ∩ 화면)
      // 그 외(group, bounds, _merged)는 bounds 불필요 (conditions 자체가 범위 지정)
      const hasRegion = params.conditions.some((c) => c.type === "region");

      const map_lv = legacy.map.getLevel();
      const payload = {
        ...payloadRef.current,
        offset: 0,
        limit: 50,
        map_lv,
        conditions: JSON.stringify(params.conditions),
      };

      if (hasRegion) {
        const bounds = legacy.discoMap.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        payload.swLat = sw.getLat();
        payload.swLng = sw.getLng();
        payload.neLat = ne.getLat();
        payload.neLng = ne.getLng();
      }

      fetchList(payload);
    },
    [fetchList, payloadRef, onMarkerSelect]
  );

  /**
   * legacy DiscoSubPageMode 초기화 (기존 getOverlay 패턴)
   */
  const createBaseOverlay = useCallback(() => {
    const baseDspmp = new legacy.DiscoSubPageModeParams();
    baseDspmp.forwardEvent = true;
    baseDspmp.helloCallback = function () {};
    baseDspmp.byeCallback = function () {
      if (legacy.estateMarkerAjax != null) {
        legacy.estateMarkerAjax.abort();
        legacy.estateMarkerAjax = null;
      }
      if (legacy.timer_redraw != null) {
        clearTimeout(legacy.timer_redraw);
        legacy.timer_redraw = null;
      }
      if (legacy.timer_area_redraw != null) {
        clearTimeout(legacy.timer_area_redraw);
        legacy.timer_area_redraw = null;
      }
      if (legacy.timer_redraw_overlay_cluster != null) {
        clearTimeout(legacy.timer_redraw_overlay_cluster);
        legacy.timer_redraw_overlay_cluster = null;
      }
      clear_map();
      legacy.reload(false);
    };
    return new legacy.DiscoSubPageMode(baseDspmp);
  }, []);

  /**
   * legacy 맵 overlay 모드 활성화/비활성화
   */
  const handleMap = useCallback(
    (isActive, isReload = false) => {
      if (isActive) {
        savedMapStateRef.current =
          legacy.jQuery?.("#map").data("original") ?? null;
        legacy.discoMap?.setMapState?.(true);

        if (!overlay.current) {
          overlay.current = createBaseOverlay();
        }

        legacy.tui_base_close();
        // namecard 등 지역페이지 갱신 차단 — dsv가 나중에 초기화될 수 있으므로 getter로 접근
        const blockLegacyUpdates = () => {
          if (legacy.dsv) legacy.dsv.blockUpdateView(true);
          legacy.jQuery(".map-name-card").css("display", "none");
        };
        blockLegacyUpdates();
        // dsv가 나중에 초기화되는 경우를 위해 짧은 딜레이 후 재시도
        setTimeout(blockLegacyUpdates, 500);

        legacy.jQuery("#map_filter_box").css("display", "none");
        if (typeof window.toggleMapToolForExplore === "function") {
          window.toggleMapToolForExplore(true);
        }

        overlay.current?.autoAddListener(
          createOverlayMode({
            getPayload: getMapPayload,
            onSelectGroup: requestSelectedGroup,
            onOpenDetail,
            drawPolygon: drawPolygonForEstate,
            onMapInteraction,
            onMapCount: setMapCount,
            MarkerClass,
            getMapService: () => estateExploreService,
            isSideviewOpen: () => {
              if (!sideviewRef?.current) return false;
              return !!sideviewRef.current.querySelector(".aside-section[data-show='true']");
            },
          })
        );

        // 타이머/ajax 정리
        if (legacy.timer_redraw != null) {
          clearTimeout(legacy.timer_redraw);
          legacy.timer_redraw = null;
        }
        if (legacy.timer_area_redraw != null) {
          clearTimeout(legacy.timer_area_redraw);
          legacy.timer_area_redraw = null;
        }
        if (legacy.reload_area_ajax) {
          legacy.reload_area_ajax?.abort();
          legacy.reload_area_ajax = null;
        }

        clear_arch_polygons?.();
        clear_land_polygons?.();
        clear_map();

        if (isReload) {
          legacy.reload(false);
        }
      } else {
        // overlay 모드 해제
        if (savedMapStateRef.current !== null) {
          legacy.discoMap?.setMapState?.(savedMapStateRef.current);
        }
        legacy.tui_base_open();
        if (legacy.dsv) legacy.dsv.blockUpdateView(false);

        if (!legacy.jQuery("#map_filter_box").is(":visible"))
          legacy.jQuery("#map_filter_box").css("display", "");
        legacy.jQuery(".map-name-card").css("display", "");
        if (typeof window.toggleMapToolForExplore === "function") {
          window.toggleMapToolForExplore(false);
        }

        overlay.current?.exitMode?.();
        overlay.current = null;
      }
    },
    [
      createBaseOverlay,
      getMapPayload,
      requestSelectedGroup,
      drawPolygonForEstate,
      onMapInteraction,
      MarkerClass,
    ]
  );

  /**
   * 맵 idle 시 isMove 세팅 + 현재 주소 업데이트
   */
  const setupMapIdleListener = useCallback(
    (onIdle) => {
      const mapInstance = legacy.map;
      if (!mapInstance) return () => {};

      const handleIdle = () => {
        setIsMove(true);
        if (onIdle) onIdle();
      };

      legacy.daum.maps.event.addListener(mapInstance, "idle", handleIdle);
      return () => {
        legacy.daum.maps.event.removeListener(mapInstance, "idle", handleIdle);
      };
    },
    []
  );

  return {
    isMove,
    setIsMove,
    mapCount,
    zoomRequired,
    handleMap,
    drawPolygonForEstate,
    requestSelectedGroup,
    getMapPayload,
    setupMapIdleListener,
  };
}
